import { generateShortId } from '@lyearn/core/utils/id';
import { splice } from '@lyearn/core/utils/immutable';
import { reorderItems } from '@/modules/corporate/components/DraggableList/utils';
import {
  DatabaseFieldTypeEnum,
  DatabaseMetadataFieldType,
  DatabaseViewFieldType,
} from '@/types/schema';

import { UpdateDatabaseFieldParams } from '../views/FormView/components/DatabaseFormEditor/types';

interface DatabaseFieldsType {
  metadataFields: DatabaseMetadataFieldType[];
  viewFields: DatabaseViewFieldType[];
}

interface AddDatabaseFieldParams extends DatabaseFieldsType {
  type: DatabaseFieldTypeEnum;
}

export const addDatabaseField = (params: AddDatabaseFieldParams) => {
  const fieldId = generateShortId();
  const { metadataFields, viewFields, type } = params;

  const metadataField: DatabaseMetadataFieldType = {
    fieldId,
    type,
    name: '',
  };
  const viewField: DatabaseViewFieldType = {
    fieldId,
    required: true,
    visible: true,
    width: 200,
    descriptionEnabled: false,
  };

  return {
    fieldId,
    metadataFields: [...metadataFields, metadataField],
    viewFields: [...viewFields, viewField],
  };
};

interface RemoveDatabaseFieldParams extends DatabaseFieldsType {
  fieldId: string;
}

export const removeDatabaseField = (params: RemoveDatabaseFieldParams) => {
  const { metadataFields, viewFields, fieldId } = params;

  return {
    metadataFields: metadataFields.filter((field) => field.fieldId !== fieldId),
    viewFields: viewFields.filter((field) => field.fieldId !== fieldId),
  };
};

export const hideDatabaseField = (params: RemoveDatabaseFieldParams) => {
  const { metadataFields, viewFields, fieldId } = params;

  return {
    metadataFields,
    viewFields: viewFields.map((field) =>
      field.fieldId === fieldId ? { ...field, visible: false } : field,
    ),
  };
};

interface DuplicateDatabaseFieldParams extends DatabaseFieldsType {
  fieldId: string;
}

export const duplicateDatabaseField = (params: DuplicateDatabaseFieldParams) => {
  const { metadataFields, viewFields, fieldId } = params;

  const metadataField = metadataFields.find((field) => field.fieldId === fieldId);
  const viewFieldIndex = viewFields.findIndex((field) => field.fieldId === fieldId);

  if (metadataField && viewFieldIndex > -1) {
    const newFieldId = generateShortId();
    const viewField = viewFields[viewFieldIndex];
    const duplicateMetadataField = { ...metadataField, fieldId: newFieldId };

    return {
      newFieldId,
      metadataFields: [...metadataFields, duplicateMetadataField],
      viewFields: splice(viewFields, viewFieldIndex + 1, 0, { ...viewField, fieldId: newFieldId }),
    };
  }

  return { metadataFields, viewFields };
};

interface ReorderDatabaseViewFieldParams extends DatabaseFieldsType {
  fromIndex: number;
  toIndex: number;
}

export const reorderDatabaseViewFields = (params: ReorderDatabaseViewFieldParams) => {
  const { metadataFields, viewFields, fromIndex, toIndex } = params;

  return {
    metadataFields,
    viewFields: reorderItems({ items: viewFields, fromIndex, toIndex }),
  };
};

type UpdateDatabaseViewFieldParams = DatabaseFieldsType & UpdateDatabaseFieldParams;

export const updateDatabaseField = (params: UpdateDatabaseViewFieldParams) => {
  const { metadataFields, viewFields, fieldId, updates } = params;

  return {
    metadataFields: metadataFields.map((field) =>
      field.fieldId === fieldId ? { ...field, ...updates.metadataField } : field,
    ),
    viewFields: viewFields.map((field) =>
      field.fieldId === fieldId ? { ...field, ...updates.viewField } : field,
    ),
  };
};
